import { useEffect, useRef, useState } from "react";
import {
  Typography,
  Snackbar,
  Alert,
  Button,
  AppBar,
  IconButton,
  Toolbar,
  DialogContent,
  Stack,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  PersonRemove as DissociateIcon,
} from "@mui/icons-material";
import {
  prettify,
  renderDeviceName,
  renderEUDateTime,
} from "../../../utils/utils";
import { LoadingError, LoadingPaper } from "../../LoadingComponents";
import { EditableInputDisplay, useEditMode } from "../../EditableComponents";

import {
  useDissociateFromPatient,
  useGetDeviceById,
  usePutDeviceById,
  devices,
} from "..";

function ViewDevice({ device_id, closeModal, _editMode }) {
  // === web service queries/mutations ===
  const deviceQuery = useGetDeviceById(device_id);
  const editMutation = usePutDeviceById(device_id);
  const dissociateMutation = useDissociateFromPatient();

  // === edit mode ===
  const {
    editMode,
    enterEditMode,
    cancelEditMode,
    confirmEditMode,
    updateEditState,
    setQueryKey,
  } = useEditMode({
    editModeEnabled: _editMode,
    updateMutation: editMutation,
    initialQueryKey: ["devices", device_id],
  });

  useEffect(() => {
    if (_editMode) enterEditMode();
  }, [_editMode]);

  const handleDissociateFromPatient = () => {
    dissociateMutation.mutate(deviceQuery.data);
  };

  return (
    <>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeModal}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {deviceQuery.isLoading ? (
        <LoadingPaper />
      ) : deviceQuery.isError ? (
        <LoadingError error={deviceQuery.error} />
      ) : (
        <DialogContent>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h5" component="div">
            Visualizza dispositivo: {prettify(deviceQuery.data.name)}
          </Typography>

          <Stack spacing={2} direction={"column"}>
            <Box sx={{ width: "100%" }}>
              {/* Azioni */}
              <Box sx={{ float: "left" }}>
                <Button // MODIFICA
                  style={{ margin: "5px" }}
                  color={editMode ? "success" : "warning"}
                  variant="contained"
                  onClick={editMode ? confirmEditMode : enterEditMode}
                >
                  <EditIcon sx={{ marginRight: "5px" }} />
                  {editMode ? "Conferma" : "Modifica"}
                </Button>

                <Button // DISSOCIA
                  style={{ margin: "5px" }}
                  variant="contained"
                  disabled={deviceQuery.data.patient_id === null}
                  onClick={handleDissociateFromPatient}
                >
                  <DissociateIcon sx={{ marginRight: "5px" }} />
                  Dissocia Dispositivo
                </Button>
              </Box>
            </Box>
            {/* Informazioni di base */}
            <Box
              sx={{
                display: "grid",
                margin: "auto",
                gridTemplateColumns: "1fr 1fr",
                width: "100%",
              }}
            >
              <>
                <Box sx={{ margin: "10px" }}>
                  <Typography>
                    <strong>Tipo di dispositivo:</strong>
                    {"     "}
                    {renderDeviceName(deviceQuery.data.device_type)}
                  </Typography>
                  <Typography>
                    <strong>Stato batteria:</strong>{" "}
                    {deviceQuery.data.battery_perc}{" "}
                    {deviceQuery.data.battery_perc !== null && "%"}
                  </Typography>
                  <Typography>
                    <strong>Ultima connessione:</strong>{" "}
                    {renderEUDateTime(deviceQuery.data.last_connection)}
                  </Typography>
                  <EditableInputDisplay
                    id={"serial_number"}
                    label={"Numero seriale:"}
                    initialValue={deviceQuery.data.serial_number}
                    setEditMode={editMode}
                    inputMode={"text"}
                    setParentValue={updateEditState}
                  />

                  <EditableInputDisplay
                    id={"note"}
                    label={"Note:"}
                    initialValue={deviceQuery.data.note}
                    setEditMode={editMode}
                    inputMode={"text"}
                    setParentValue={updateEditState}
                  />
                </Box>
                <Box sx={{ margin: "10px" }}>
                  <EditableInputDisplay
                    id={"patient_id"}
                    label={"Paziente:"}
                    initialValue={deviceQuery.data.patient_id}
                    setEditMode={editMode}
                    inputMode={"select"}
                    selectType={"patients"}
                    selectQueryKey={["patients"]}
                    setParentValue={updateEditState}
                  />
                  <EditableInputDisplay
                    id={"name"}
                    label={"Descrizione:"}
                    initialValue={deviceQuery.data.name}
                    setEditMode={editMode}
                    inputMode={"text"}
                    setParentValue={updateEditState}
                  />
                  <EditableInputDisplay
                    id={"is_online"}
                    label={"Stato (online)"}
                    initialValue={deviceQuery.data.is_online}
                    setEditMode={editMode}
                    inputMode={"bool"}
                    setParentValue={updateEditState}
                  />
                  <EditableInputDisplay
                    id={"is_installed"}
                    label={"Stato (installato)"}
                    initialValue={deviceQuery.data.is_installed}
                    setEditMode={editMode}
                    inputMode={"bool"}
                    setParentValue={updateEditState}
                  />
                </Box>
              </>
            </Box>
          </Stack>
        </DialogContent>
      )}
    </>
  );
}

export default ViewDevice;
